import {AbstractMesh, Observable} from "@babylonjs/core";
import log, {Logger} from "loglevel";
import {TextEvent} from "./inputTextView";

/**
 * SystemKeyboardInput provides text input using the WebXR system keyboard (Meta Quest native keyboard).
 * This is an alternative to the VirtualKeyboard-based InputTextView for devices that support
 * the WebXR system keyboard API.
 *
 * Usage:
 * - Check isSupported() before using
 * - Call show(mesh) to display the system keyboard
 * - Listen to onTextObservable for text changes
 * - Call dispose() when XR session ends
 */
export class SystemKeyboardInput {
    private logger: Logger = log.getLogger('SystemKeyboardInput');
    public readonly onTextObservable: Observable<TextEvent> = new Observable<TextEvent>();

    private inputElement: HTMLInputElement | null = null;
    private currentMeshId: string | null = null;
    private xrSession: XRSession | null = null;
    private visibilityHandler: ((ev: XRSessionEvent) => void) | null = null;

    constructor() {
        this.logger.debug('SystemKeyboardInput created');
    }

    /**
     * Check if the system keyboard is supported for the given XR session.
     */
    public static isSupported(session: XRSession | null): boolean {
        if (!session) {
            return false;
        }
        // TypeScript doesn't have this property in the type definitions yet
        return (session as any).isSystemKeyboardSupported === true;
    }

    /**
     * Initialize with an XR session. Should be called when entering immersive mode.
     */
    public setSession(session: XRSession): void {
        this.xrSession = session;

        // Set up visibility change listener for keyboard lifecycle
        this.visibilityHandler = (ev: XRSessionEvent) => {
            const visState = (ev.target as XRSession).visibilityState;
            this.logger.debug('XR visibility changed:', visState);

            if (visState === 'visible' && this.inputElement) {
                // Keyboard was dismissed, finalize input
                this.finalizeInput();
            }
        };

        session.addEventListener('visibilitychange', this.visibilityHandler);

        // Clean up when session ends
        session.addEventListener('end', () => {
            this.dispose();
        });
    }

    /**
     * Show the system keyboard for editing the given mesh's text.
     */
    public show(mesh: AbstractMesh): void {
        if (!this.xrSession) {
            this.logger.warn('Cannot show system keyboard: no XR session');
            return;
        }

        this.currentMeshId = mesh.id;

        // Create input element if it doesn't exist
        if (!this.inputElement) {
            this.inputElement = document.createElement('input');
            this.inputElement.type = 'text';
            this.inputElement.id = 'system-keyboard-input';

            // Position off-screen to avoid visual interference
            // but keep it in DOM for keyboard to work
            this.inputElement.style.position = 'fixed';
            this.inputElement.style.left = '-9999px';
            this.inputElement.style.top = '0';
            this.inputElement.style.opacity = '0';
            this.inputElement.style.pointerEvents = 'none';

            // Handle blur event (keyboard dismissed)
            this.inputElement.onblur = () => {
                this.logger.debug('Input blurred');
                this.finalizeInput();
            };

            document.body.appendChild(this.inputElement);
        }

        // Set initial value from mesh metadata
        const initialText = mesh.metadata?.text || '';
        this.inputElement.value = initialText;

        this.logger.debug('Showing system keyboard for mesh:', mesh.id, 'initial text:', initialText);

        // Focus to trigger the system keyboard
        this.inputElement.focus();
    }

    /**
     * Hide the keyboard and discard any changes.
     */
    public hide(): void {
        if (this.inputElement) {
            this.inputElement.blur();
        }
        this.currentMeshId = null;
    }

    /**
     * Finalize the input and notify observers.
     */
    private finalizeInput(): void {
        if (!this.currentMeshId || !this.inputElement) {
            return;
        }

        const text = this.inputElement.value.trim();
        this.logger.debug('Finalizing input:', text, 'for mesh:', this.currentMeshId);

        // Notify observers with the final text (or null if empty)
        this.onTextObservable.notifyObservers({
            id: this.currentMeshId,
            text: text.length > 0 ? text : null
        });

        this.currentMeshId = null;
    }

    /**
     * Clean up resources. Should be called when XR session ends.
     */
    public dispose(): void {
        this.logger.debug('Disposing SystemKeyboardInput');

        if (this.visibilityHandler && this.xrSession) {
            this.xrSession.removeEventListener('visibilitychange', this.visibilityHandler);
            this.visibilityHandler = null;
        }

        if (this.inputElement) {
            this.inputElement.remove();
            this.inputElement = null;
        }

        this.xrSession = null;
        this.currentMeshId = null;
    }
}
